import React, { useState } from "react";
import { Animated, StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { Ionicons } from "@expo/vector-icons";

import Colors from "../../../../Resources/Color";
import useChevronRotation from "../../../hooks/useChevronRotation";
import {
  formatCalorieValue,
  formatMacroValue,
  formatMicronutrientValue,
  formatPercentageValue,
  UI_MACRO_DISPLAY_ORDER,
} from "../../shared/nutritionFormatters";

export function RecipeEditorSummarySection({
  t,
  isMobile,
  recipeTotals,
  macroBreakdown,
  macronutrientsTitle,
  summaryMacroItems,
  micronutrientSummaryItems,
  microsExpanded,
  setMicrosExpanded,
  contextTitle,
  summaryReferenceLabel = null,
}) {
  const [hoveredMacro, setHoveredMacro] = useState(null);
  const [hoveredSupportCard, setHoveredSupportCard] = useState(null);
  const [isMicrosToggleHovered, setIsMicrosToggleHovered] = useState(false);
  const microsChevronRotation = useChevronRotation(
    microsExpanded,
    "closedDownOpenUp",
  );
  const hasSelectedMacronutrients =
    Number(recipeTotals.fats ?? 0) > 0 ||
    Number(recipeTotals.carbs ?? 0) > 0 ||
    Number(recipeTotals.protein ?? 0) > 0;
  const stackedBarSegments = UI_MACRO_DISPLAY_ORDER.map((key) => {
    const segmentConfig = {
      protein: {
        colorStyle: styles.stackedBarSegmentProtein,
        flex: macroBreakdown.proteinCalories,
      },
      carbs: {
        colorStyle: styles.stackedBarSegmentCarbs,
        flex: macroBreakdown.carbsCalories,
      },
      fats: {
        colorStyle: styles.stackedBarSegmentFats,
        flex: macroBreakdown.fatsCalories,
      },
    }[key];

    return {
      key,
      ...segmentConfig,
    };
  }).filter((segment) => segment.flex > 0);

  return (
    <>
      {contextTitle ? (
        <Text
          style={[
            styles.summaryContextValue,
            summaryReferenceLabel ? styles.summaryContextValueWithReferenceLabel : null,
          ]}
          numberOfLines={2}
        >
          {contextTitle}
        </Text>
      ) : null}
      {summaryReferenceLabel ? (
        <Text style={styles.summaryReferenceLabel}>
          {summaryReferenceLabel}
        </Text>
      ) : null}

      <View style={styles.summarySupportGrid}>
        <View
          onMouseEnter={
            !isMobile ? () => setHoveredSupportCard("energy") : undefined
          }
          onMouseLeave={
            !isMobile ? () => setHoveredSupportCard(null) : undefined
          }
          style={[
            styles.summarySupportCard,
            hoveredSupportCard === "energy"
              ? styles.summarySupportCardActive
              : null,
          ]}
        >
          <Text style={styles.summarySupportLabel}>{t("energy-string")}</Text>
          <Text style={styles.summarySupportValue}>
            {formatCalorieValue(recipeTotals.calories)}
            <Text style={styles.summarySupportUnit}> kcal</Text>
          </Text>
        </View>
        <View
          onMouseEnter={
            !isMobile ? () => setHoveredSupportCard("fiber") : undefined
          }
          onMouseLeave={
            !isMobile ? () => setHoveredSupportCard(null) : undefined
          }
          style={[
            styles.summarySupportCard,
            hoveredSupportCard === "fiber"
              ? styles.summarySupportCardActive
              : null,
          ]}
        >
          <Text style={styles.summarySupportLabel}>{t("fiber-string")}</Text>
          <Text style={styles.summarySupportValue}>
            {formatMacroValue(recipeTotals.fiber)}
            <Text style={styles.summarySupportUnit}> g</Text>
          </Text>
        </View>
      </View>

      <View style={styles.summaryOverviewCard}>
        <Text style={styles.summaryChartTitle}>{macronutrientsTitle}</Text>

        <View style={styles.summaryTrackWrap}>
          <View
            style={[
              styles.stackedBarTrack,
              {
                backgroundColor: hasSelectedMacronutrients
                  ? Colors.whiteColor
                  : Colors.lightGrayColor,
              },
            ]}
          >
            {stackedBarSegments.map((segment) => (
              <View
                key={segment.key}
                onMouseEnter={
                  !isMobile ? () => setHoveredMacro(segment.key) : undefined
                }
                onMouseLeave={!isMobile ? () => setHoveredMacro(null) : undefined}
                style={[
                  styles.stackedBarSegment,
                  segment.colorStyle,
                  {
                    flex: segment.flex,
                    opacity:
                      hoveredMacro && hoveredMacro !== segment.key ? 0.55 : 1,
                    transform: [
                      { scaleY: hoveredMacro === segment.key ? 1.08 : 1 },
                    ],
                    zIndex: hoveredMacro === segment.key ? 2 : 1,
                  },
                ]}
              />
            ))}
          </View>
        </View>

        <View style={styles.summaryMacroList}>
          {summaryMacroItems.map((item, index) => (
            <View
              key={item.key}
              onMouseEnter={!isMobile ? () => setHoveredMacro(item.key) : undefined}
              onMouseLeave={!isMobile ? () => setHoveredMacro(null) : undefined}
              style={[
                styles.summaryMacroRow,
                index !== 0 ? styles.summaryMacroRowBorder : null,
              ]}
            >
              <View
                style={[
                  styles.summaryMacroRowInner,
                  hoveredMacro === item.key
                    ? styles.summaryMacroRowInnerActive
                    : null,
                  hoveredMacro && hoveredMacro !== item.key
                    ? styles.summaryMacroRowInnerMuted
                    : null,
                ]}
              >
                <View style={styles.summaryMacroRowMain}>
                  <View
                    style={[
                      styles.summaryMacroDot,
                      { backgroundColor: item.color },
                    ]}
                  />
                  <Text style={styles.summaryMacroRowLabel}>{item.label}</Text>
                </View>
                <View style={styles.summaryMacroValueWrap}>
                  <Text style={styles.summaryMacroRowValue}>
                    {formatMacroValue(item.grams)} g
                  </Text>
                  <Text style={styles.summaryMacroRowShare}>
                    {formatPercentageValue(item.share)}%
                  </Text>
                </View>
              </View>
            </View>
          ))}
        </View>
      </View>

      <View style={styles.summaryMicrosCard}>
        <TouchableOpacity
          activeOpacity={0.85}
          onPress={() => setMicrosExpanded((current) => !current)}
          onMouseEnter={
            !isMobile ? () => setIsMicrosToggleHovered(true) : undefined
          }
          onMouseLeave={
            !isMobile ? () => setIsMicrosToggleHovered(false) : undefined
          }
          style={styles.summaryMicrosToggle}
        >
          <View style={styles.summaryMicrosToggleMain}>
            <Text style={styles.summaryMicrosTitle}>
              {t("addFoodModalMicrosTitle-string")}
            </Text>
          </View>
          <Animated.View
            style={{ transform: [{ rotate: microsChevronRotation }] }}
          >
            <Ionicons
              name="chevron-down"
              size={14}
              color={
                isMicrosToggleHovered ? Colors.darkgrayColor : Colors.grayColor
              }
            />
          </Animated.View>
        </TouchableOpacity>

        {microsExpanded ? (
          <View style={styles.summaryMicroList}>
            {micronutrientSummaryItems.map((item, index) => (
              <View
                key={item.key}
                style={[
                  styles.summaryMicroRow,
                  index !== 0 ? styles.summaryMicroRowBorder : null,
                ]}
              >
                <Text style={styles.summaryMicroLabel}>{item.label}</Text>
                <Text style={styles.summaryMicroValue}>
                  {formatMicronutrientValue(item.value)}
                  <Text style={styles.summaryMicroUnit}> {item.unit}</Text>
                </Text>
              </View>
            ))}
          </View>
        ) : null}
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  summaryContextValue: {
    fontSize: 13,
    lineHeight: 18,
    fontWeight: "500",
    color: Colors.blackColor,
    marginBottom: 14,
  },
  summaryContextValueWithReferenceLabel: {
    marginBottom: 4,
  },
  summaryReferenceLabel: {
    fontSize: 10,
    lineHeight: 13,
    fontWeight: "400",
    color: Colors.grayColor,
    marginBottom: 16,
  },
  summaryOverviewCard: {
    marginTop: 16,
  },
  summaryTrackWrap: {
    marginTop: 12,
  },
  summaryMacroList: {
    marginTop: 12,
  },
  summaryMacroRow: {
    width: "100%",
  },
  summaryMacroRowInner: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    paddingHorizontal: 4,
    paddingVertical: 11,
    transition: "all 300ms ease-in-out",
    borderRadius: 8,
  },
  summaryMacroRowBorder: {
    borderTopWidth: 0.5,
    borderTopColor: Colors.borderColor,
  },
  summaryMacroRowInnerActive: {
    transform: [{ scale: 1.008 }],
  },
  summaryMacroRowInnerMuted: {
    opacity: 0.6,
  },
  summaryMacroRowMain: {
    flex: 1,
    minWidth: 0,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  summaryMacroDot: {
    width: 8,
    height: 8,
    borderRadius: 999,
  },
  summaryMacroRowLabel: {
    fontSize: 12,
    fontWeight: "500",
    color: Colors.darkgrayColor,
  },
  summaryMacroValueWrap: {
    alignItems: "flex-end",
    gap: 2,
  },
  summaryMacroRowValue: {
    fontSize: 13,
    fontWeight: "500",
    color: Colors.blackColor,
  },
  summaryMacroRowShare: {
    fontSize: 10,
    fontWeight: "500",
    color: Colors.grayColor,
  },
  summarySupportGrid: {
    flexDirection: "row",
    gap: 10,
  },
  summarySupportCard: {
    flex: 1,
    minWidth: 0,
    borderRadius: 12,
    borderWidth: 0.5,
    borderColor: Colors.borderColor,
    backgroundColor: Colors.whiteColor,
    paddingHorizontal: 12,
    paddingVertical: 12,
    transition: "all 300ms ease-in-out",
  },
  summarySupportCardActive: {
    transform: [{ scale: 1.008 }],
  },
  summarySupportLabel: {
    fontSize: 10,
    fontWeight: "600",
    color: Colors.grayColor,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  summarySupportValue: {
    marginTop: 5,
    fontSize: 16,
    fontWeight: "500",
    color: Colors.blackColor,
  },
  summarySupportUnit: {
    fontSize: 11,
    fontWeight: "400",
    color: Colors.grayColor,
  },
  summaryMicrosCard: {
    marginTop: 16,
  },
  summaryMicrosToggle: {
    minHeight: 28,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  summaryMicrosToggleMain: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  summaryMicrosTitle: {
    fontSize: 12,
    fontWeight: "500",
    color: Colors.grayColor,
  },
  summaryMicroList: {
    marginTop: 10,
  },
  summaryMicroRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 10,
    paddingHorizontal: 2,
    paddingVertical: 8,
  },
  summaryMicroRowBorder: {
    borderTopWidth: 0.5,
    borderTopColor: Colors.borderColor,
  },
  summaryMicroLabel: {
    flex: 1,
    minWidth: 0,
    fontSize: 11,
    fontWeight: "400",
    color: Colors.darkgrayColor,
  },
  summaryMicroValue: {
    fontSize: 11,
    fontWeight: "500",
    color: Colors.blackColor,
    textAlign: "right",
  },
  summaryMicroUnit: {
    fontSize: 10,
    fontWeight: "400",
    color: Colors.grayColor,
  },
  stackedBarTrack: {
    width: "100%",
    height: 8,
    borderRadius: 999,
    backgroundColor: Colors.lightGrayColor,
    overflow: "hidden",
    flexDirection: "row",
    gap: 2,
  },
  stackedBarSegment: {
    height: "100%",
    borderRadius: 2,
    transition: "all 300ms ease-in-out",
  },
  stackedBarSegmentCarbs: {
    backgroundColor: Colors.carbsColor,
  },
  stackedBarSegmentProtein: {
    backgroundColor: Colors.proteinColor,
  },
  stackedBarSegmentFats: {
    backgroundColor: Colors.fatsColor,
  },
  summaryChartTitle: {
    fontSize: 12,
    fontWeight: "500",
    color: Colors.grayColor,
    marginTop: 8,
    marginBottom: 10,
  },
});
